import { useFlags } from "launchdarkly-react-client-sdk";
import { useMemo } from "react";

import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import HistoryIcon from "@mui/icons-material/History";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { QUICK_LAUNCHER_FEATURE } from "constants/feature-flags";
import { openQuickLauncher } from "features/app-layout/quick-launcher/quick-launcher.slice";
import { useSideMenuChoice } from "features/app-layout/side-menu-choice/use-side-menu-choice.hook";
import { useSwitchSideMenu } from "features/app-layout/side-menu-choice/use-switch-side-menu.hook";
import { selectCurrentUser } from "features/auth/auth-slice";

import { MENU, MENU_GROUPS, MenuGroup } from "./menu";
import SideMenuShell, {
  SideMenuRow,
  SideMenuSection
} from "./side-menu-shell.component";
import useSideMenuBadges, { BadgedMenuItem } from "./use-side-menu-badges.hook";
import useVisibleMenuItems from "./use-visible-menu-items.hook";

// Re-exported for the existing `import { MENU } from "../layout-side-menu"` call
// sites; the array itself now lives in `./menu` so the visibility hook can read
// it without an import cycle back through this component.
export { MENU };
export {
  SIDE_MENU_COLLAPSED_STORAGE_KEY,
  SIDE_MENU_MIN_WIDTH_PX,
  SIDE_MENU_RAIL_WIDTH_PX,
  SIDE_MENU_WIDGET_CLEARANCE_PX
} from "./side-menu-shell.component";

const toRow = (item: BadgedMenuItem): SideMenuRow => ({
  key: item.to,
  to: item.to,
  primary: item.primary,
  Icon: item.Icon,
  isFallbackPath: item.isFallbackPath,
  badgeContent: item.badgeContent,
  tooltip: item.tooltip
});

/**
 * The main app's side menu (ENG-2535): the entries this person can reach, in
 * their groups, on the shared shell. Settings is pinned in the footer, with the
 * way back to the classic menu above it and Administration below for Suno staff.
 */
export default function LayoutSideMenu() {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);

  const flags = useFlags();
  const quickLauncherOn = Boolean(flags[QUICK_LAUNCHER_FEATURE]);
  // ENG-2535: while the redesign is a choice, the footer offers the way back.
  const { offered: switchOffered } = useSideMenuChoice();
  const { switchToClassic } = useSwitchSideMenu();
  const hasAdminPanel = Boolean(currentUser?.has_access_to_admin_panel);

  // Visibility (roles, permissions, flags, practice/user columns) lives in its
  // own hook so the quick launcher can offer exactly the destinations this user
  // can reach; the badge counts in another. This component keeps the grouping.
  const badgedItems = useSideMenuBadges(useVisibleMenuItems());

  // Groups in MENU_GROUPS order, each only when something in it is visible to this
  // user. Home has no group and sits right after "Getting started"; Settings is pinned.
  const { sections, footerRows } = useMemo(() => {
    const byGroup = (group: MenuGroup) =>
      badgedItems.filter((item) => item.group === group).map(toRow);
    const home = badgedItems
      .filter((item) => !item.group && !item.footer)
      .map(toRow);
    const [firstGroup, ...restGroups] = MENU_GROUPS;
    const ordered: SideMenuSection[] = [
      { key: firstGroup, title: firstGroup, rows: byGroup(firstGroup) },
      { key: "home", rows: home },
      ...restGroups.map((group) => ({
        key: group,
        title: group,
        rows: byGroup(group)
      }))
    ];
    const footer: SideMenuRow[] = [
      ...(switchOffered
        ? [
            {
              key: "classic-menu",
              onClick: () => switchToClassic("footer"),
              primary: "Classic menu",
              Icon: HistoryIcon,
              tooltip: "Go back to the menu you had before"
            }
          ]
        : []),
      ...badgedItems.filter((item) => item.footer).map(toRow),
      ...(hasAdminPanel
        ? [
            {
              key: "administration",
              to: "/administration",
              primary: "Administration",
              Icon: AdminPanelSettingsOutlinedIcon,
              tooltip: "Suno staff: manage practices, clinics and users",
              staff: true
            }
          ]
        : [])
    ];
    return {
      sections: ordered.filter((section) => section.rows.length > 0),
      footerRows: footer
    };
  }, [badgedItems, switchOffered, switchToClassic, hasAdminPanel]);

  return (
    <SideMenuShell
      navLabel="Main"
      sections={sections}
      footerRows={footerRows}
      showTour
      showSearch={quickLauncherOn}
      onSearch={() => dispatch(openQuickLauncher("side_menu"))}
    />
  );
}
